import {
  Theme,
  TypographyVariant,
  WithTheme,
  bem,
  cx,
  interpolateFont,
  interpolateTextColor,
} from '@mint/core';
import { useFocus, useFormControlClassNames, useValue } from '../shared';
import React from 'react';
import { TextareaBaseProps } from './textarea-base-props';
import styled from 'styled-components';

/**
 * Base input root class name applied to the input element.
 */
export const ROOT_CLASS_NAME = 'base-input';

/**
 * Helper function to create input element properties.
 * @param props TextareaBase component properties.
 * @param theme Resolved theme instance.
 * @param focused Flag indicating if the input has focus.
 */
const makeInputProps = (
  props: TextareaBaseProps,
  focused: boolean,
  hasValue: boolean,
): React.TextareaHTMLAttributes<HTMLTextAreaElement> => {
  const { className, dense } = props;
  const classes = bem(ROOT_CLASS_NAME);
  return {
    className: cx(
      ROOT_CLASS_NAME,
      useFormControlClassNames(ROOT_CLASS_NAME, { ...props, focused }),
      dense && classes.dense(),
      hasValue ? classes.hasValue() : classes.empty(),
      className,
    ),
  };
};

/**
 * `TextareaBase` component implementation before styling.
 */
const TextareaBaseComponent = React.forwardRef<
  HTMLTextAreaElement,
  TextareaBaseProps
>(
  (props, ref): React.ReactElement => {
    const {
      children,
      className,
      dense,
      disabled,
      hidePlaceholder,
      onBlur,
      onChange,
      onFocus,
      ...rest
    } = props;

    const [focused, bindFocus] = useFocus({ onBlur, onFocus });
    const [value, handleChange] = useValue({ onChange });

    const textareaProps = {
      ...makeInputProps(props, focused, value.length > 0),
      ...rest,
      ...bindFocus,
      onChange: handleChange,
      ref,
    };

    return <textarea {...textareaProps} />;
  },
);

TextareaBaseComponent.displayName = 'TextareaBase';

const pickFont = (props: WithTheme<TextareaBaseProps>): TypographyVariant =>
  props.dense ? 'dense' : 'control';

const interpolateHidePlaceholder = (
  props: WithTheme<TextareaBaseProps>,
): string => {
  if (props.hidePlaceholder) {
    return `opacity: 0;`;
  } else {
    return '';
  }
};

export const TextareaBase = styled(TextareaBaseComponent)`
  background: rgba(0, 0, 0, 0);
  border: 0;
  outline: 0;
  padding: 0;
  width: 100%;

  ${interpolateFont(pickFont, 'text.primary')}

  ::placeholder {
    ${interpolateTextColor('text.hint')}
    ${interpolateHidePlaceholder}
  }
`;
